import './DealersEditPage.css'
import { useEffect, useRef, useState } from 'react'
import { AlertMessage, DcsPermissions, IParamsType, IRootState } from 'types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
    AttachmentsForm,
    BillingForm,
    ClosedCasesForm,
    ContactHistoryForm,
    ContactsForm,
    DealerInformationForm,
    DefaultButton,
    DomainsForm,
    EchoSignForm,
    HistoryGrid,
    OpenCasesForm,
    ProductsForm,
    SaveButton,
    StickyHeaderWrapper,
    TransactionsForm,
    MessageAlert,
    SideNav,
    SideNavContent,
    SideNavMenu,
    SideNavMenuItem,
    SideNavMenuSection,
    SideNavWrapper,
    SummaryForm
} from 'components'
import NotesForm from 'components/PageForms/Cases/NotesForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormHandle } from '@progress/kendo-react-form'
import { Badge } from 'react-bootstrap'
import { LS_SIDEBAR_MENU_STATE } from 'core/consts'
import { isMobile } from 'react-device-detect'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { V1ApiDealersClient } from 'core/http-common'
import { getErrors } from 'core/helpers'
import { useSelector } from 'react-redux'
import { DealerDto, ProductTypeEnum } from '@v1DealersApi'
import { NoteTypeEnum } from 'generated/v1/v1SharedApi'
import { ISAGridRef } from 'components/Grid/SAGrid'
import moment from 'moment'
import { IFormDateSpanSelectorRef } from 'components/Form/FormDateSpanSelector'

export enum EditDealerSectionTypeEnum {
    DEALER_SUMMARY = 'dealer-summary',
    DEALER_INFORMATION = 'dealer-information',
    DOMAINS = 'domains',
    HISTORY = 'history',
    OPEN_CASES = 'open-cases',
    CLOSED_CASES = 'closed-cases',
    CONTACTS = 'contacts',
    NOTES = 'notes',
    ECHO_SIGN = 'echo-sign',
    ATTACHMENTS = 'attachments',
    CONTACT_HISTORY = 'contact-history',
    TRANSACTIONS = 'transactions',
    BILLING = 'billing',
    PRODUCTS = 'products',
}

export interface IParams extends IParamsType {
    id: string
    dealerId: string
    section: string
}

const DealersEditPage = () => {
    const { id, section } = useParams<IParams>()
    const navigate = useNavigate()

    const location = useLocation()
    const widthRef = useRef(window.innerWidth)
    const baseUrl = `/dealers/edit/${id}`
    const loading = false ///useSelector((state: IRootState) => state.app.loading)
    const [dealer, setDealer] = useState<DealerDto>()

    const [showSideNavMenu, setShowSideNavMenu] = useState<boolean>(true)
    const [selectedSection, setSelectedSection] = useState<EditDealerSectionTypeEnum>(
        section ? section as EditDealerSectionTypeEnum : EditDealerSectionTypeEnum.DEALER_SUMMARY
    )
    const [alert, setAlert] = useState<AlertMessage>()
    const [formIsModified, setFormIsModified] = useState<boolean>(false)

    //when the form is modified this is the dto that will be saved when the user clicks save in the save or discard modal
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [dtoToSave, setDtoToSave] = useState<any>()

    const sectionsToHideSaveButton = [
        EditDealerSectionTypeEnum.NOTES
    ]

    useEffect(() => {
        const storedSidebarStatus = localStorage.getItem(LS_SIDEBAR_MENU_STATE)
        if (storedSidebarStatus)
            setShowSideNavMenu(storedSidebarStatus === 'true')
    }, [])

    ///close sidebar on specific widths
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth === widthRef.current)
                return

            if (window.innerWidth < 1325) {
                setShowSideNavMenu(false)
                localStorage.setItem(LS_SIDEBAR_MENU_STATE, 'false')

            } else {
                setShowSideNavMenu(true)
                localStorage.setItem(LS_SIDEBAR_MENU_STATE, 'true')
            }

            widthRef.current = window.innerWidth
        }

        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])

    const fetchDealer = async () => {
        if (!id || !id.trim() || isNaN(Number(id))) {
            // navigate(redirectUrl)
            return
        }

        try {
            //await dispatch(startLoadingAction())
            setAlert({ variant: 'info', messages: [] })
            let dealer = await V1ApiDealersClient.getDealer(parseInt(id))
            setDealer(dealer)
        }
        catch (e) {
            setAlert({ variant: 'danger', messages: getErrors(e) })
        }
        finally {
            window.scrollTo(0, 0)
        }
    }

    useEffect(() => {
        fetchDealer()
    }, [id])


    const changeSection = (sectionType: EditDealerSectionTypeEnum) => {
        if (isMobile) {
            setShowSideNavMenu(false)
        }

        if (selectedSection === sectionType)
            return

        //if form is not modified just change section and update url
        if (!formIsModified) {
            setSelectedSection(sectionType)
            //special redirect that doesn't cause page render with side nav
            window.history.replaceState(null, '', `${baseUrl}/${sectionType}`)
            window.scrollTo(0, 0)
            return
        }
        window.scrollTo(0, 0)
    }

    const hasEditInventoryPermission = true// hasPermission(DcsPermissions.EditInventory)

    const editable = hasEditInventoryPermission

    const dateSpanRef = useRef<IFormDateSpanSelectorRef>(null)
    const formDateSpanSelectorRef = useRef<FormHandle>(null)
    const dealerHistoryGridRef = useRef<ISAGridRef>(null)

    const maxDate = moment().endOf('day').toDate()

    const searchByDate = async (start: Date, end: Date) => {
        formDateSpanSelectorRef.current?.valueSetter('startDate', start)
        formDateSpanSelectorRef.current?.valueSetter('endDate', end)
        dealerHistoryGridRef.current?.resetAndFetch()

    }
    const SectionHandler = (selectedStep: EditDealerSectionTypeEnum) => {
        switch (selectedStep) {
            case EditDealerSectionTypeEnum.DEALER_SUMMARY:
                return <SummaryForm
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditDealerSectionTypeEnum.DEALER_INFORMATION:
                return <DealerInformationForm
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditDealerSectionTypeEnum.DOMAINS:
                return <DomainsForm
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditDealerSectionTypeEnum.HISTORY:
                return <HistoryGrid
                    type={'Dealer'}
                    getHistoryData={undefined}
                    historyGridRef={dealerHistoryGridRef}
                    dateSpanRef={dateSpanRef}
                    searchByDate={searchByDate}
                    maxDate={maxDate}
                    historyDateSpanFormRef={formDateSpanSelectorRef}

                />
            case EditDealerSectionTypeEnum.OPEN_CASES:
                return <OpenCasesForm
                    dealerId={id ?? ''}
                    editable={editable}
                    formIsModified={formIsModified}
                />
            case EditDealerSectionTypeEnum.CLOSED_CASES:
                return <ClosedCasesForm
                    dealerId={id ?? ''}
                    editable={editable}
                    formIsModified={formIsModified}
                />
            case EditDealerSectionTypeEnum.CONTACTS:
                return <ContactsForm
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditDealerSectionTypeEnum.NOTES:
                return <NotesForm
                    noteTypeId={dealer?.dealerId}
                    noteType={NoteTypeEnum.Dealer}
                    editable={editable}
                    formIsModified={formIsModified}
                    hideDateOptions={true}
                />
            case EditDealerSectionTypeEnum.ECHO_SIGN:
                return <EchoSignForm
                    formIsModified={formIsModified}
                    editable={editable}
                />
            case EditDealerSectionTypeEnum.ATTACHMENTS:
                return <AttachmentsForm
                    editable={editable}
                    formIsModified={formIsModified}
                />
            case EditDealerSectionTypeEnum.CONTACT_HISTORY:
                return <ContactHistoryForm
                    editable={editable}
                    formIsModified={formIsModified}
                />
            case EditDealerSectionTypeEnum.TRANSACTIONS:
                return <TransactionsForm
                    editable={editable}
                    formIsModified={formIsModified}
                />
            case EditDealerSectionTypeEnum.BILLING:
                return <BillingForm
                    editable={editable}
                    formIsModified={formIsModified}
                />
            case EditDealerSectionTypeEnum.PRODUCTS:
                return <ProductsForm
                    dealerId={parseInt(id ?? '')}
                    productType={ProductTypeEnum.Dealer}
                    formIsModified={formIsModified}
                    editable={editable}
                />
            default:
                return <SummaryForm
                    formIsModified={formIsModified}
                    editable={editable}
                />
        }
    }

    const sectionActive = (array: Array<EditDealerSectionTypeEnum>) => {
        return array.some(a => a === selectedSection)
    }

    const itemActive = (item: EditDealerSectionTypeEnum) => item === selectedSection

    // if (!hasPermission(DcsPermissions.EditInventory))
    //     return (
    //         <MessageAlert
    //             alert={{
    //                 variant: 'danger',
    //                 messages: [DEFAULT_PERMISSION_MESSAGE]
    //             }}
    //         />
    //     )

    return (
        <div id='DealersEditPage' className='hidden-print'>
            <MessageAlert alert={alert} />

            <StickyHeaderWrapper>
                <div className='row p-2 mt-2'>
                    <div className='col-md-4' style={{ alignSelf: 'center' }}>
                        <span className='pageHeader'>
                            <h3>
                                <a className='btn-sm btn btn-default'
                                    title='Return to List'
                                // onClick={() => back()}
                                >
                                    <FontAwesomeIcon icon={['fas', 'reply']} />
                                </a>
                                <span>
                                    {' '}Dealer Car Search Demo
                                </span>
                            </h3>
                        </span>
                    </div>
                    <div className='col-md-8 text-md-end text-start mt-2 mt-lg-0' style={{ alignSelf: 'center' }}>
                        <div>
                            {
                                formIsModified
                                    ? <i id='unsavedMessage' className='font-weight-bold me-1'><Badge bg='danger'><FontAwesomeIcon icon={['fas', 'exclamation-circle']} /> unsaved changes</Badge></i>
                                    : <></>
                            }
                            <DefaultButton
                                className='me-1 btn-sm'
                                variant='default'
                                disabled={loading}
                                onClick={() => navigate(`/cases/add?dealerid=${id}`)}>
                                <FontAwesomeIcon icon={['fas', 'plus-circle']} /> {' '}
                                Add Case
                            </DefaultButton>
                            <DefaultButton variant='default' className='btn-sm me-1'><FontAwesomeIcon icon={faEnvelope} /> {' '}Re-Send Welcome Email</DefaultButton>
                            {!sectionsToHideSaveButton.find(x => x === selectedSection) &&
                                <SaveButton className='btn btn-sm' />
                            }
                        </div>
                    </div>
                </div>
                <div className='section-divider'></div>
            </StickyHeaderWrapper>

            <SideNavWrapper>
                <SideNav
                    showMenu={showSideNavMenu}>
                    <div className='p-2'>
                        <div className='text-center'>
                            <a
                                title='Click for Dealer Information'
                                className='cursor-pointer'
                                onClick={() => changeSection(EditDealerSectionTypeEnum.DEALER_INFORMATION)}
                            >
                                <img
                                    src={'https://www.dealercarsearch.com/_next/static/images/logo-b2287f11d8bf33d9eff1a17015262504.png'}
                                    alt='Dealer Logo'
                                    className='img-fluid my-2'
                                    style={{ width: '180px', backgroundColor: '#fff', padding: '3px' }}
                                />

                            </a>
                        </div>
                        <div className='p-2'>
                            <Badge className={dealer?.active ? 'bg-success shadow-sm w-100 p-2 mb-2 cursor-pointer' : 'bg-danger shadow-sm w-100 p-2 mb-2 cursor-pointer'} title='Click to change status.'>Status: {dealer?.active ? 'ACTIVE' : 'INACTIVE'}</Badge>
                            <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2' style={{ fontSize: '12px' }}>Dealer ID: {dealer?.dealerId}</div>
                            <div className='bg-white shadow-sm rounded border mb-2 w-100 p-2' style={{ fontSize: '12px' }}>Group ID: {dealer?.dealerGroupId}</div>
                            <div className='bg-white shadow-sm rounded border w-100 p-2' style={{ fontSize: '12px' }}>Last Login: Feb Wed 21 2024</div>
                        </div>
                    </div>
                    <div className='border-top'>
                        <SideNavMenu>
                            <SideNavMenuItem
                                title='Summary'
                                active={selectedSection === EditDealerSectionTypeEnum.DEALER_SUMMARY}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.DEALER_SUMMARY)}
                            />
                            <SideNavMenuItem
                                title='Dealer Information'
                                active={selectedSection === EditDealerSectionTypeEnum.DEALER_INFORMATION}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.DEALER_INFORMATION)}
                            />

                            <SideNavMenuSection
                                title='Cases'
                                active={sectionActive([
                                    EditDealerSectionTypeEnum.OPEN_CASES,
                                    EditDealerSectionTypeEnum.CLOSED_CASES,
                                ])}>
                                <SideNavMenuItem
                                    title='Open Cases (3)'
                                    active={itemActive(EditDealerSectionTypeEnum.OPEN_CASES)}
                                    onClick={() => changeSection(EditDealerSectionTypeEnum.OPEN_CASES)} />
                                <SideNavMenuItem
                                    title='Closed Cases (9)'
                                    active={selectedSection === EditDealerSectionTypeEnum.CLOSED_CASES}
                                    onClick={() => changeSection(EditDealerSectionTypeEnum.CLOSED_CASES)} />
                            </SideNavMenuSection>

                            <SideNavMenuItem
                                title='Products'
                                active={selectedSection === EditDealerSectionTypeEnum.PRODUCTS}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.PRODUCTS)}
                            />

                            <SideNavMenuItem
                                title='Domains'
                                active={selectedSection === EditDealerSectionTypeEnum.DOMAINS}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.DOMAINS)}
                            />
                            <SideNavMenuSection
                                title='History'
                                active={sectionActive(
                                    [
                                        EditDealerSectionTypeEnum.HISTORY,
                                        EditDealerSectionTypeEnum.CONTACT_HISTORY,
                                    ])}>
                                <SideNavMenuItem
                                    title='Dealer History'
                                    active={itemActive(EditDealerSectionTypeEnum.HISTORY)}
                                    onClick={() => { changeSection(EditDealerSectionTypeEnum.HISTORY) }}
                                />
                                <SideNavMenuItem
                                    title='Contact History'
                                    active={itemActive(EditDealerSectionTypeEnum.CONTACT_HISTORY)}
                                    onClick={() => changeSection(EditDealerSectionTypeEnum.CONTACT_HISTORY)}
                                />
                            </SideNavMenuSection>

                            <SideNavMenuItem
                                title='Contacts'
                                active={selectedSection === EditDealerSectionTypeEnum.CONTACTS}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.CONTACTS)}
                            />

                            <SideNavMenuItem
                                title='Notes'
                                active={selectedSection === EditDealerSectionTypeEnum.NOTES}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.NOTES)}
                            />

                            <SideNavMenuSection
                                title='Documents'
                                active={sectionActive(
                                    [
                                        EditDealerSectionTypeEnum.ECHO_SIGN,
                                        EditDealerSectionTypeEnum.ATTACHMENTS,
                                    ])}>
                                <SideNavMenuItem
                                    title='Echo Sign'
                                    active={selectedSection === EditDealerSectionTypeEnum.ECHO_SIGN}
                                    onClick={() => changeSection(EditDealerSectionTypeEnum.ECHO_SIGN)}
                                />
                                <SideNavMenuItem
                                    title='Attachments'
                                    active={selectedSection === EditDealerSectionTypeEnum.ATTACHMENTS}
                                    onClick={() => changeSection(EditDealerSectionTypeEnum.ATTACHMENTS)}
                                />
                            </SideNavMenuSection>

                            <SideNavMenuItem
                                title='Transactions'
                                active={selectedSection === EditDealerSectionTypeEnum.TRANSACTIONS}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.TRANSACTIONS)}
                            />

                            <SideNavMenuItem
                                title='Billing'
                                active={selectedSection === EditDealerSectionTypeEnum.BILLING}
                                onClick={() => changeSection(EditDealerSectionTypeEnum.BILLING)}
                            />

                        </SideNavMenu>
                    </div>

                </SideNav>
                <SideNavContent showMenu={showSideNavMenu}
                    onToggleMenu={() => {
                        setShowSideNavMenu(!showSideNavMenu)
                        localStorage.setItem(LS_SIDEBAR_MENU_STATE, (!showSideNavMenu).toString())
                    }
                    }>
                    <div className={`ms-2 mb-4 ${showSideNavMenu ? 'shown' : 'hidden'}`}>
                        {SectionHandler(selectedSection)}
                    </div>
                </SideNavContent>
            </SideNavWrapper>
        </div>
    )
}
export default DealersEditPage
